"use client";

import { Trophy, Info } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { useBtcPrice } from "@/hooks/useBtcPrice";
import { formatNumber } from "@/app/plaza/lucky-wheel/utils/numberFormatter";
import { calculatePrizeValue, formatUsdValue } from "@/app/plaza/lucky-wheel/utils/prizeValueCalculator";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { GrandPrizeInfo } from "../../hooks/useLuckyWheelDynamics";

interface GrandPrizeDisplayProps {
  grandPrize: GrandPrizeInfo | null;
  isLoading: boolean;
  language: "zh" | "en";
}

export default function GrandPrizeDisplay({ grandPrize, isLoading, language }: GrandPrizeDisplayProps) {
  const { btcPrice } = useBtcPrice();
  const [usdValue, setUsdValue] = useState<number>(0);
  const router = useRouter();

  const translations = {
    zh: {
      noPrize: "炉火静待，大奖筹备中",
      comingSoon: "大奖即将现世，敬请期待",
      totalStock: "总数",
      wonCount: "已出",
      remainingCount: "剩余",
      estimated: "预估奖励，实际以中奖时为准",
      rewardPerUnit: "每份奖励",
      specialPrize: "特等奖",
      firstPrize: "一等奖",
      secondPrize: "二等奖",
    },
    en: {
      noPrize: "Forge waiting, prize preparing",
      comingSoon: "Grand prize coming soon",
      totalStock: "Total",
      wonCount: "Won",
      remainingCount: "Left",
      estimated: "Estimated reward, actual may vary",
      rewardPerUnit: "Reward Per Unit",
      specialPrize: "Special",
      firstPrize: "1st Prize",
      secondPrize: "2nd Prize",
    },
  };

  const t = translations[language];
  const TOKEN_ICON = "https://api.odin.fun/v1/token/2rfi/image";

  // 七件符宝名称硬映射 (同步 EnhancedRankings)
  const getTreasureName = useCallback((level: number) => {
    const treasureNames = {
      zh: {
        1: "👑 圣宝·奥丁圣印",
        2: "🌳 灵宝·世界树果",
        3: "🕊️ 御宝·瓦尔基里羽",
      },
      en: {
        1: "👑 Holy Seal of Odin",
        2: "🌳 Yggdrasil Fruit",
        3: "🕊️ Valkyrie Feather",
      },
    };
    return treasureNames[language][level as 1 | 2 | 3] || `Level ${level}`;
  }, [language]);

  // 计算 USD 价值
  useEffect(() => {
    async function calculateValue() {
      if (!btcPrice || btcPrice === 0 || !grandPrize) return;
      const prizeType = { Balance: grandPrize.totalAmount };
      const result = await calculatePrizeValue(prizeType, btcPrice);
      setUsdValue(result.usdValue);
    }
    calculateValue();
  }, [grandPrize, btcPrice]);

  if (isLoading) {
    return (
      <div className="h-24 flex items-center justify-center bg-orange-50/30 dark:bg-orange-950/10 rounded-xl animate-pulse">
        <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!grandPrize) {
    return (
      <div className="flex flex-col items-center justify-center h-28 rounded-xl border-2 border-dashed border-light-border dark:border-dark-border bg-light-surface/50 dark:bg-dark-surface/50">
        <div className="w-10 h-10 rounded-full bg-light-surface dark:bg-dark-surface flex items-center justify-center mb-2 shadow-sm">
          <Trophy className="w-5 h-5 text-gray-400" />
        </div>
        <p className="text-sm font-bold text-gray-500 dark:text-gray-400">{t.noPrize}</p>
        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">{t.comingSoon}</p>
      </div>
    );
  }

  const fullPrizeName = getTreasureName(grandPrize.level);

  const handleCardClick = () => {
    router.push("/plaza/lucky-wheel");
  };

  return (
    <div className="space-y-2">
      {/* 核心大奖卡片 */}
      <div 
        onClick={handleCardClick}
        className="relative p-3 rounded-xl bg-gradient-to-br from-amber-400/20 to-orange-500/20 dark:from-amber-400/10 dark:to-orange-500/10 border border-orange-200/50 dark:border-orange-900/30 overflow-hidden group shadow-sm cursor-pointer hover:bg-orange-500/5 transition-all active:scale-[0.98]"
      >
        {/* 背景装饰光效 */}
        <div className="absolute top-0 right-0 -mt-2 -mr-2 w-16 h-16 bg-yellow-400/20 rounded-full blur-xl animate-pulse" />
        
        <div className="relative z-10">
          {/* 奖项名称与等级标签 */}
          <div className="flex items-center gap-2 mb-2.5">
            <span className="text-lg">{fullPrizeName.split(" ")[0]}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-bold text-amber-900 dark:text-amber-100 truncate">
                  {fullPrizeName.split(" ").slice(1).join(" ")}
                </span>
                <Badge className="bg-orange-500 hover:bg-orange-600 text-[9px] h-3.5 px-1.5 border-0 font-bold flex-shrink-0">
                  {grandPrize.level === 1 ? t.specialPrize : 
                   grandPrize.level === 2 ? t.firstPrize : 
                   t.secondPrize}
                </Badge>
              </div>
            </div>
          </div>

          {/* 美元价值与明细布局优化 */}
          <div className="flex items-center justify-between gap-1 mb-3.5">
            {/* 左侧：美元价值 */}
            <div className="flex-shrink-0">
              <div className="text-2xl font-black text-orange-600 dark:text-orange-400 tracking-tighter leading-none">
                {formatUsdValue(usdValue)}
              </div>
            </div>
            
            {/* 分割线 - 压缩间距 */}
            <div className="w-px h-7 bg-orange-200/40 dark:bg-orange-900/30 mx-0.5" />

            {/* 右侧：明细与醒目标签 */}
            <div className="flex-1 flex flex-col justify-center min-w-0">
              {/* 第一行：代币总额 (图标在前) */}
              <div className="flex items-center gap-1.5 text-sm font-black text-amber-900/90 dark:text-amber-100/90 truncate leading-none">
                <img src={TOKEN_ICON} alt="icon" className="w-3.5 h-3.5 rounded-full flex-shrink-0" />
                <div className="flex items-center gap-0.5">
                  <span>{formatNumber(Number(grandPrize.totalAmount))}</span>
                  {grandPrize.isDynamic && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3 cursor-help opacity-40 ml-0.5" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[200px] text-[10px]">
                          {t.estimated} ({formatNumber(Number(grandPrize.baseAmount))} + {formatNumber(Number(grandPrize.dynamicAmount))})
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>
              
              {/* 第二行：说明标签 (仅亮色文字，无边框背景，紧贴上行) */}
              <div className="text-[9px] font-bold text-orange-600 dark:text-orange-400 leading-none mt-0.5">
                {t.rewardPerUnit}
              </div>
            </div>
          </div>

          {/* 库存统计 */}
          <div className="flex flex-col gap-1.5 pt-2 border-t border-orange-200/30 dark:border-orange-900/20">
            <div className="flex items-center justify-between text-[10px] font-bold">
              <div className="flex items-center gap-4">
                <span className="text-gray-500 dark:text-gray-400">
                  {t.totalStock} <span className="text-amber-900 dark:text-amber-100">{grandPrize.totalStock}</span>
                </span>
                <span className="text-green-600 dark:text-green-400">
                  {t.wonCount} <span className="text-amber-900 dark:text-amber-100">{grandPrize.wonStock}</span>
                </span>
                <span className="text-orange-600 dark:text-orange-400">
                  {t.remainingCount} <span className="text-amber-900 dark:text-amber-100">{grandPrize.remainingStock}</span>
                </span>
              </div>
              <div className="text-[9px] text-gray-400 dark:text-gray-500 opacity-60">
                {Math.round((grandPrize.wonStock / grandPrize.totalStock) * 100)}%
              </div>
            </div>
            
            <div className="h-1 w-full bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: `${(grandPrize.wonStock / grandPrize.totalStock) * 100}%` }}
                className="h-full bg-gradient-to-r from-orange-400 to-orange-600"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
